import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	FormLabel,
	Input,
	Flex,
	Button,
	Text,
} from '@chakra-ui/react';
import { Select as ChakraSelect } from 'chakra-react-select';
import Locale from '../../../utils/translations';
import { Template, Sample, LanguageOptionsModel } from '../../../utils/interfaces';
import { BonfireAPI, ApiType } from '../../../models/BonfireApi';
import config from '../../../utils/config';

interface AddTranslationTestModalProps {
	isOpen: boolean;
	onClose: () => void;
	name: string;
	setName: (val: string) => void;
	sample: Sample | null;
	selectedTemplateIds: string[];
	setSelectedTemplateIds: (val: string[]) => void;
	translationLanguages: string;
	setTranslationLanguages: (val: string) => void;
	selectedTemplateBcapIds?: Record<string, string | null>;
	setSelectedTemplateBcapIds?: (val: string[]) => void;
	isCreating: boolean;
	error: string;
	onAdd: () => void;
}

const AddTranslationTestModal: React.FC<AddTranslationTestModalProps> = ({
	isOpen,
	onClose,
	name,
	setName,
	sample,
	selectedTemplateIds,
	setSelectedTemplateIds,
	translationLanguages,
	setTranslationLanguages,
	isCreating,
	error,
	onAdd,
}) => {
	const [templates, setTemplates] = useState<Template[]>([]);
	const [loading, setLoading] = useState(false);
	const [languagesList, setLanguagesList] = React.useState<
		{ label: string; value: string }[] | []
	>([]);

	const loadLanguagesList = async () => {
		try {
			const api = new BonfireAPI(ApiType.BonfireCore);
			const data = await api.get('/languages/get');

			const languagesOptions = data.map((language: LanguageOptionsModel) => {
				return { label: language.name, value: language.code };
			});

			setLanguagesList(languagesOptions);
		} catch (err) {
			console.error(err);
		}
	};

	const loadTemplates = useCallback(async () => {
		if (!sample?.sampleId) {
			return;
		}
		setLoading(true);
		const api = new BonfireAPI(ApiType.QaApi);
		try {
			const url = `${config.qa_api.url}/aiModelComparisonsTemplates/list?sampleId=${sample.sampleId}`;
			const response = await api.get(url);
			setTemplates(Array.isArray(response) ? response : []);
		} finally {
			setLoading(false);
		}
	}, [sample?.sampleId]);

	useEffect(() => {
		loadLanguagesList();
		if (isOpen && sample?.sampleId) {
			loadTemplates();
		}
	}, [isOpen, sample?.sampleId, loadTemplates]);

	const templateOptions = useMemo(
		() =>
			templates.map(t => ({
				value: t.templateId,
				label: t.name,
			})),
		[templates],
	);

	const selectedOptions = useMemo(
		() => templateOptions.filter(o => selectedTemplateIds.includes(o.value)),
		[templateOptions, selectedTemplateIds],
	);

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered>
			<ModalOverlay />
			<ModalContent maxW="430px" w="90%">
				<ModalHeader>
					<Text fontWeight="bold">{Locale.get('Add Translation Test')}</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<FormLabel>{Locale.get('Name')}</FormLabel>
					<Input
						mb={3}
						bg="white"
						placeholder={Locale.get('Name')}
						value={name}
						onChange={e => setName(e.target.value)}
					/>

					<Text mb={3} fontWeight="bold">
						{Locale.get('Sample')}: {sample?.name || ''}
					</Text>

					<FormLabel>{Locale.get('Templates')}</FormLabel>
					<ChakraSelect
						isMulti
						isLoading={loading}
						options={templateOptions}
						value={selectedOptions}
						onChange={vals => {
							const ids = Array.isArray(vals) ? vals.map(v => v.value) : [];
							setSelectedTemplateIds(ids);
						}}
						placeholder={Locale.get('Select templates')}
						chakraStyles={{
							control: p => ({
								...p,
								_light: { backgroundColor: 'white' },
								_dark: { backgroundColor: 'black' },
								shadow: '0px 4px 4px 0px rgba(74, 74, 74, 0.04)',
							}),
							dropdownIndicator: p => ({
								...p,
								_light: { backgroundColor: 'white' },
								_dark: { backgroundColor: 'black' },
							}),
						}}
					/>

					<FormLabel mt={3}>{Locale.get('Translation Language')}</FormLabel>
					<ChakraSelect
						isMulti
						options={languagesList}
						value={languagesList.filter(opt =>
							(translationLanguages || '')
								.split(',')
								.map(l => l.trim())
								.filter(Boolean)
								.includes(opt.value),
						)}
						onChange={vals => {
							const langs = Array.isArray(vals) ? vals.map(v => v.value) : [];
							setTranslationLanguages(langs.join(','));
						}}
						placeholder={Locale.get('Select translation languages')}
						chakraStyles={{
							control: p => ({
								...p,
								_light: { backgroundColor: 'white' },
								_dark: { backgroundColor: 'black' },
								shadow: '0px 4px 4px 0px rgba(74, 74, 74, 0.04)',
							}),
							dropdownIndicator: p => ({
								...p,
								_light: { backgroundColor: 'white' },
								_dark: { backgroundColor: 'black' },
							}),
						}}
					/>

					{!!error && (
						<Text color="red.500" fontSize="sm" mb={2}>
							{error}
						</Text>
					)}
				</ModalBody>
				<ModalFooter>
					<Flex gap={2} w="100%">
						<Button
							variant="bePrimary"
							onClick={onAdd}
							isLoading={isCreating}
							isDisabled={isCreating}
							w="50%"
						>
							{Locale.get('Add')}
						</Button>
						<Button onClick={onClose} isDisabled={isCreating} w="50%">
							{Locale.get('Cancel')}
						</Button>
					</Flex>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default React.memo(AddTranslationTestModal);
